import React, { useState, useEffect } from 'react';
import gql from 'graphql-tag';
import { CdsIcon, useInjector, useLazyQuery, useMutation } from '@vendure/admin-ui/react';
import { trashIcon, plusIcon } from '@cds/core/icon';
import { NotificationService, I18nService } from '@vendure/admin-ui/core';
import { Table, MantineProvider } from '@mantine/core';
import { Modal } from 'quatru-frontend-lib-common';
import { mantineTheme } from '../../utils/theme';

const GET_INTEGRATION_LEADS = gql`
  query getIntegrationLead($partnerId: String!, $token: String) {
    getIntegrationLead(partnerId: $partnerId, token: $token) {
      __typename
      ... on IntegrationLeads {
        id
        partnerSecret
        source
        sellerId
        createdAt
        updatedAt
      }
      ... on NotFoundError {
        errorCode
        message
      }
      ... on InternalServerError {
        errorCode
        message
      }
    }
  }
`;

const SAVE_INTEGRATION_LEAD = gql`
  mutation SaveIntegrationLead($input: SaveIntegrationLeadInput!) {
    saveIntegrationLead(input: $input) {
      __typename
      ... on IntegrationLeads {
        id
        partnerSecret
        source
        sellerId
        updatedAt
      }
      ... on ErrorResult {
        errorCode
        message
      }
    }
  }
`;

interface IntegrationLeadsData {
  getIntegrationLead:
    | {
        __typename: 'IntegrationLeads';
        id: string;
        partnerSecret: string[] | null;
        source: string | null;
        sellerId: string;
        createdAt: string;
        updatedAt: string;
      }
    | { __typename: 'NotFoundError'; errorCode: string; message: string }
    | { __typename: 'InternalServerError'; errorCode: string; message: string };
}

const DEFAULT_SOURCE = 'CASA_YES';

export function IntegrationLeadsListComponent() {
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [partnerSecrets, setPartnerSecrets] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [source, setSource] = useState<string>(DEFAULT_SOURCE);
  const [isDirty, setIsDirty] = useState(false);

  const i18n = useInjector(I18nService);
  const notificationService = useInjector(NotificationService);

  const [getIntegrationLeads, { data, loading, error }] =
    useLazyQuery<IntegrationLeadsData>(GET_INTEGRATION_LEADS);
  const [saveIntegrationLead, { loading: saving }] = useMutation(SAVE_INTEGRATION_LEAD);

  useEffect(() => {
    const parts = window.location.pathname.split('/');
    const id = parts[parts.length - 1];
    if (id && !isNaN(Number(id))) {
      setSellerId(id);
      console.log('Fetching integration leads for seller ID:', id);
      getIntegrationLeads({ partnerId: id, token: undefined});
    }
  }, []);

  useEffect(() => {
    if (!data?.getIntegrationLead) return;
    const result = data.getIntegrationLead;
    if (result.__typename === 'IntegrationLeads') {
      setPartnerSecrets(result.partnerSecret ?? []);
      setSource(result.source || DEFAULT_SOURCE);
      setIsDirty(false);
    } else if (result.__typename === 'NotFoundError') {
      setPartnerSecrets([]);
      setSource(DEFAULT_SOURCE);
      setIsDirty(false);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      notificationService.error(
        i18n.translate('integration-leads.load-error') || 'Failed to load integration leads.'
      );
    }
  }, [error]);

  const handleAddSecret = () => {
    const trimmed = newSecret.trim();
    if (!trimmed) {
      notificationService.error(
        i18n.translate('integration-leads.empty-input') ||
          'Please enter a value before adding.'
      );
      return;
    }
    if (partnerSecrets.includes(trimmed)) {
      notificationService.error(
        i18n.translate('integration-leads.duplicate') || 'That value already exists in the list.'
      );
      return;
    }
    setPartnerSecrets(prev => [...prev, trimmed]);
    setNewSecret('');
    setIsModalOpen(false);
    setIsDirty(true);
  };

  const handleRemoveSecret = (secret: string) => {
    setPartnerSecrets(prev => prev.filter(s => s !== secret));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!sellerId) {
      notificationService.error(
        i18n.translate('integration-leads.missing-seller') || 'Seller ID not found.'
      );
      return;
    }
    try {
      const result: any = await saveIntegrationLead({
        input: {
          sellerId,
          integrationLeadIds: partnerSecrets,
          source: source || DEFAULT_SOURCE
        }
      });
      const payload = result?.saveIntegrationLead;
      if (payload?.__typename === 'IntegrationLeads') {
        notificationService.success(
          i18n.translate('integration-leads.saved-success') ||
            'Integration leads saved successfully.'
        );
        setIsDirty(false);
        getIntegrationLeads({ partnerId: sellerId, token: undefined});
      } else {
        notificationService.error(
          payload?.message ||
            i18n.translate('integration-leads.save-error') ||
            'Failed to save integration leads.'
        );
      }
    } catch (err) {
      notificationService.error(
        i18n.translate('integration-leads.save-error') || 'Failed to save integration leads.'
      );
      console.error(err);
    }
  };


  return (
    <div style={{ padding: '12px', maxWidth: '100%', fontSize: '14px' }}>
      {!sellerId ? (
        <div
          style={{
            padding: '24px',
            border: '1px dashed #ccc',
            borderRadius: '6px',
            textAlign: 'center',
            color: '#666',
            fontSize: '13px'
          }}>
          {i18n.translate('integration-leads.create-seller-first') ||
            'Save the seller first. Integration Leads can only be configured after the seller has been created.'}
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
              gap: '8px'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>
                {i18n.translate('integration-leads.source') || 'Source'}:
              </label>
              <input
                type='text'
                value={source}
                onChange={e => {
                  setSource(e.target.value);
                  setIsDirty(true);
                }}
                style={{
                  padding: '4px 6px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '12px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type='button'
                className='button primary'
                onClick={() => setIsModalOpen(true)}>
                <CdsIcon icon={plusIcon} />{' '}
                {i18n.translate('integration-leads.add') || 'Add Secret'}
              </button>
              <button
                type='button'
                className='button primary'
                onClick={handleSave}
                disabled={!isDirty || saving}>
                {saving
                  ? i18n.translate('integration-leads.saving') || 'Saving...'
                  : i18n.translate('integration-leads.save') || 'Save'}
              </button>
            </div>
          </div>

          <MantineProvider theme={mantineTheme}>
            <Table style={{ fontSize: '12px' }}>
              <Table.Thead>
                <Table.Tr style={{ height: '32px' }}>
                  <Table.Th style={{ fontSize: '14px', padding: '4px' }}>
                    {i18n.translate('integration-leads.partner-secret') || 'Partner Secret'}
                  </Table.Th>
                  <Table.Th
                    style={{ fontSize: '14px', padding: '4px', width: '120px' }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <Table.Tr>
                    <Table.Td
                      colSpan={2}
                      align='center'
                      style={{ fontSize: '12px', padding: '8px' }}>
                      Loading...
                    </Table.Td>
                  </Table.Tr>
                ) : partnerSecrets.length === 0 ? (
                  <Table.Tr>
                    <Table.Td
                      colSpan={2}
                      align='center'
                      style={{ fontSize: '12px', padding: '8px', color: '#666' }}>
                      {i18n.translate('integration-leads.no-secrets') ||
                        'No partner secrets registered yet.'}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  partnerSecrets.map(secret => (
                    <Table.Tr key={secret}>
                      <Table.Td style={{ fontSize: '12px', padding: '4px' }}>{secret}</Table.Td>
                      <Table.Td style={{ fontSize: '12px', padding: '4px' }}>
                        <button
                          className='button-small'
                          type='button'
                          onClick={() => handleRemoveSecret(secret)}>
                          <CdsIcon icon={trashIcon} />
                          {i18n.translate('integration-leads.remove') || 'Remove'}
                        </button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </MantineProvider>

          <Modal Open={isModalOpen} handleClose={() => setIsModalOpen(false)}>
            <div>
              <div style={{ width: '100%' }}>
                <h6 style={{ marginBottom: '16px' }}>
                  {i18n.translate('integration-leads.add-secret-title') || 'Add Partner Secret'}
                </h6>
                <input
                  type='text'
                  placeholder={
                    i18n.translate('integration-leads.partner-secret-placeholder') ||
                    'Partner secret'
                  }
                  value={newSecret}
                  onChange={e => setNewSecret(e.target.value)}
                  style={{
                    width: '100%',
                    marginBottom: '16px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    type='button'
                    onClick={() => {
                      setIsModalOpen(false);
                      setNewSecret('');
                    }}
                    className='button secondary'
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer'
                    }}>
                    {i18n.translate('integration-leads.cancel') || 'Cancel'}
                  </button>
                  <button
                    type='button'
                    onClick={handleAddSecret}
                    className='button primary'
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer'
                    }}>
                    {i18n.translate('integration-leads.confirm') || 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
